import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { NavigationDomainInterface } from '../../../interfaces';
import { NavigationStateService, VisibleNavigationService } from '../../../services';
import { MobileBottomBarComponent, MobileBottomBarItem } from './mobile-bottom-bar.component';
import { MobileNavigationDrawerComponent } from './mobile-navigation-drawer.component';

/** Mesmo limite usado no layout para esconder a sidebar: smartphones até 767 px. */
export const MOBILE_NAVIGATION_QUERY = '(max-width: 47.9375rem)';

/** Atalhos da barra inferior. Aparecem só se o usuário tiver acesso ao destino. */
const SHORTCUTS: ReadonlyArray<{ id: string; label: string; icon: string; domainId: string; route?: string }> = [
  { id: 'home', label: 'Início', icon: 'layout-dashboard', domainId: 'dashboard' },
  { id: 'sales', label: 'Vendas', icon: 'shopping-bag', domainId: 'sales', route: '/vendas/historico' },
  { id: 'stock', label: 'Estoque', icon: 'boxes', domainId: 'catalog', route: '/estoque' },
  { id: 'financial', label: 'Financeiro', icon: 'wallet', domainId: 'financial' }
];

@Component({
  selector: 'app-mobile-navigation',
  standalone: true,
  imports: [MobileBottomBarComponent, MobileNavigationDrawerComponent],
  template: `
    @if (isMobile()) {
      <app-mobile-bottom-bar
        [items]="shortcuts()"
        [activeDomainId]="nav.activeDomainId()"
        [menuOpen]="menuOpen()"
        (menuToggle)="menuOpen.set(true)" />

      <app-mobile-navigation-drawer
        [open]="menuOpen()"
        [domains]="domains()"
        (closed)="menuOpen.set(false)" />
    }
  `,
  host: { '(window:popstate)': 'menuOpen.set(false)' }
})
export class MobileNavigationComponent {
  protected readonly nav = inject(NavigationStateService);
  private readonly visibleNavigation = inject(VisibleNavigationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly menuOpen = signal(false);
  protected readonly domains = this.visibleNavigation.domains;
  protected readonly isMobile = signal(false);

  protected readonly shortcuts = computed<MobileBottomBarItem[]>(() => {
    const domains = this.domains();

    return SHORTCUTS.reduce<MobileBottomBarItem[]>((items, shortcut) => {
      const domain = domains.find((candidate) => candidate.id === shortcut.domainId);
      const route = domain && this.resolveRoute(domain, shortcut.route);

      return route ? [...items, { ...shortcut, route }] : items;
    }, []);
  });

  constructor() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const query = window.matchMedia(MOBILE_NAVIGATION_QUERY);
      const sync = (): void => this.isMobile.set(query.matches);

      sync();
      query.addEventListener('change', sync);
      this.destroyRef.onDestroy(() => query.removeEventListener('change', sync));
    }

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => this.menuOpen.set(false));
  }

  private resolveRoute(domain: NavigationDomainInterface, preferred?: string): string | null {
    if (domain.directRoute) {
      return domain.directRoute;
    }

    const items = domain.sections.flatMap((section) => section.items);

    return (items.find((item) => item.route === preferred) ?? items[0])?.route ?? null;
  }
}
